<!--
  TextWithNotesHtml.vue

  Renderizador dos textos com content_format = 'html'. Irmão do TextWithNotes.vue,
  que continua servindo os textos em texto puro e NÃO deve ser alterado enquanto os
  dois existirem — ele renderiza toda a base hoje.

  Recebe as mesmas props e emite os mesmos eventos que o irmão, então o
  TextDisplayer só precisa escolher qual dos dois monta.

  O que difere, e só isto:

   • a raiz é <div>, não <span>. HTML válido não permite <p> dentro de <p> nem
     <ul> dentro de <span>; com blocos no conteúdo o navegador "consertaria" a
     árvore sozinho — e árvore remontada é mapeamento de offset bagunçado.
   • a segmentação deixa de ser fatiamento linear de uma string e passa a ser
     travessia da árvore: cada text node é recortado nas fronteiras de nota que
     caem dentro dele.
   • os offsets são medidos na PROJEÇÃO do HTML (utils/htmlProjection.js), não no
     HTML. É a mesma régua que o backend usa para gravar as notas.
   • mapRangeToContentOffsets usa closest('[data-offset]'). No texto puro os spans
     nunca aninhavam e um node.contains() bastava; aqui o vocabulário de classes
     produz <span> de conteúdo que aninha com o <span data-offset> deste
     renderizador, e contains() pegaria o ancestral errado.
-->
<script setup>
import { ref, computed, h, onMounted, onUnmounted, watch, nextTick } from "vue";
import { toPlainText, PROJECTION_BREAK_TAGS } from "@/utils/htmlProjection";
import {
  computeNoteSegments,
  sliceNodeBySegments,
  chunkPieces,
} from "@/utils/htmlNoteSegments";
import { useSettingStore } from "@/stores/useSettingStore";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTagStore } from "@/stores/useTagStore";
import { useUserStore } from "@/stores/useUserStore";
// ── NOVO: imports para "Editar Nota" no menu de contexto ─────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import EditNoteForm from "@/components/Note/EditNoteForm.vue";
import EditTagForm from "@/components/Tag/EditTagForm.vue";
import SettingsModal from "@/components/Setting/SettingsModal.vue";
// ── NOVO: pilha flutuante com os avatares dos criadores das notas visíveis ───
import NoteCreatorsStack from "./NoteCreatorsStack.vue";

const settingStore = useSettingStore();
const noteStore = useNoteStore();
const tagStore = useTagStore();
const userStore = useUserStore();
// ── NOVO: store do vínculo Membro-Titular ────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

const props = defineProps({
  content: { type: String, required: true },
  notes: { type: Array, default: () => [] },

  userId: { type: Number, required: true },
  createdByUserId: { type: Number, required: true },
  updatedByUserId: { type: Number, required: true },

  hasExponent: { type: Boolean, default: true },
  hasHighlights: { type: Boolean, default: true },
  /**
   * Mostra o fólio na margem — a página do documento ORIGINAL, gravada na
   * importação como âncora vazia (`<a id="lit-page-5">`). Serve para citar o
   * mesmo trecho de quem tem o impresso na mão.
   */
  hasPageMarks: { type: Boolean, default: false },
});

const emit = defineEmits([
  "selection-made",
  "note-clicked",
  "notes-focused",
  "note-details",
  "request-delete",
]);

const containerRef = ref(null);

// ── NOVO: âncora da pilha de criadores ───────────────────────────────────────
// A raiz deste componente é um <span> inline dentro de <p class="text-body">, e
// por isso não pode ancorar um filho posicionado. A pilha usa o mesmo container
// de rolagem já usado pelo auto-scroll do highlightTextNoteId.
const scrollAnchorEl = ref(null);

const showSettingsModal = ref(false);

watch(
  () => props.notes,
  (newNotes) => {
    console.log(
      "Novas notas recebidas:",
      Array.isArray(newNotes) ? newNotes.length : 0
    );
  },
  { deep: true, immediate: true }
);

// ── Clique esquerdo ──────────────────────────────────────────────────────────
// Clique no EXPOENTE: ele já aponta para uma nota específica, então é sempre
// "localize esta ficha na lista" — o NoteList rola até ela e ilumina.
function onNoteClick(notes) {
  if (notes && notes.length > 0) {
    const topNote = notes[0];
    if (topNote && topNote.noteId) {
      emit("note-clicked", topNote.noteId);
    }
  }
}

// Clique no TRECHO destacado. A segmentação fatia o texto em cada fronteira de
// range, então o segmento clicado carrega exatamente as notas que cobrem aquele
// ponto — e é o tamanho dessa cobertura que decide o comportamento:
//
//  • uma nota  → não há ambiguidade a resolver. Mesmo caminho do expoente:
//    localiza a ficha na lista completa, rolando e iluminando.
//  • duas ou mais (interseção de ranges) → aí sim não dá para adivinhar qual
//    delas o leitor quis, e a lista passa a exibir só as envolvidas.
function onSegmentClick(notes) {
  if (!notes || notes.length === 0) return;

  const noteIds = notes
    .map((note) => note?.noteId)
    .filter((noteId) => noteId !== null && noteId !== undefined);

  if (noteIds.length === 0) return;

  if (noteIds.length === 1) {
    emit("note-clicked", noteIds[0]);
    return;
  }

  emit("notes-focused", noteIds);
}

// ── Menu de contexto ─────────────────────────────────────────────────────────
const contextMenu = ref({
  visible: false,
  x: 0,
  y: 0,
  notes: [],
  canEditTag: false,
  tagId: null,
  // ── NOVO: campos para o item "Editar Nota" ──
  canEditNote: false,
  noteId: null,
  // ── NOVO: campos para o item "Apagar Nota" ──
  canDeleteNote: false,
});

const showEditTagForm = ref(false);

// ── NOVO: estado do EditNoteForm aberto pelo menu de contexto ────────────────
const showEditNoteForm = ref(false);
const selectedNoteIdForEdit = ref(null);

// ── NOVO: permissões efetivas do vínculo Membro-Titular para notas ───────────
const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);
const memberCanEditNote = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.edit
);
// ── NOVO: guard de delete espelhado do NoteCrudDropdown ──────────────────────
const memberCanDeleteNote = computed(
  () =>
    memberNotePermissions.value === null || memberNotePermissions.value.delete
);

function closeContextMenu() {
  contextMenu.value.visible = false;
}

async function onNoteRightClick(notes, event) {
  // Se já existe uma seleção de texto e o clique direito ocorreu dentro dela,
  // o menu nativo do navegador deve prevalecer. Não chama preventDefault().
  if (isRightClickInsideCurrentTextSelection(event)) {
    closeContextMenu();
    return;
  }

  event.preventDefault();

  // Quando o botão direito foi usado para arrastar/selecionar texto,
  // o contextmenu seguinte precisa morrer aqui para não abrir menu nenhum.
  if (suppressNextContextMenu.value || rightButtonSelection.value.moved) {
    event.stopPropagation();
    closeContextMenu();
    return;
  }

  if (!notes || notes.length === 0) return;

  // Fecha menu anterior imediatamente
  contextMenu.value.visible = false;

  const topNote = notes[0];

  // Determina a tag primária da nota para verificar permissão
  const tagIds = topNote?.tagIds ?? [];
  const tagIsPrimary = topNote?.tagIsPrimary ?? [];
  let primaryTagId = null;

  if (tagIds.length > 0) {
    const primaryIdx = tagIsPrimary.findIndex((v) => v === true);
    primaryTagId = tagIds[primaryIdx >= 0 ? primaryIdx : 0] ?? null;
  }

  // Verifica permissão de edição da tag (busca leve — só se houver tag)
  let canEditTag = false;
  if (primaryTagId) {
    try {
      const tag = await tagStore.getTagById(primaryTagId);
      const logged = (userStore.currentUser?.userName ?? "")
        .trim()
        .toLowerCase();
      if (logged === "sys") {
        canEditTag = true;
      } else {
        canEditTag = tag?.createdByUserId === userStore.currentUser?.userId;
      }
    } catch {
      canEditTag = false;
    }
  }

  // ── NOVO: calcula permissão de edição da nota (mesma lógica do NoteCrudDropdown) ──
  // canShowEditNote = memberCanEditNote && (isNoteCreator || isSysUser || canEditNote)
  const currentUserId = userStore.currentUser?.userId ?? null;
  const isNoteCreator = currentUserId === topNote?.createdByUserId;
  const isSysUser = (userStore.currentUser?.userName ?? "") === "Sys";
  const noteCanEditFlag = !!topNote?.canEditNote;
  const canShowEditNote =
    memberCanEditNote.value && (isNoteCreator || isSysUser || noteCanEditFlag);

  // ── NOVO: canShowDelete espelha NoteCrudDropdown ──────────────────────────
  // memberCanDeleteNote && (canShowEditNote || isActivityCreator || isTextCreator)
  // TextWithNotes não tem acesso a activityCreatedByUserId/textCreatedByUserId,
  // mas tem createdByUserId (do texto). Espelhamos com o que está disponível.
  const isTextCreator = currentUserId === props.createdByUserId;
  const canShowDelete =
    memberCanDeleteNote.value && (canShowEditNote || isTextCreator);

  // Posiciona o menu onde o mouse clicou
  // Aguarda nextTick para garantir que o menu seja renderizado antes de ajustar posição
  contextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    notes,
    canEditTag,
    tagId: primaryTagId,
    // ── NOVO: dados da nota para o item "Editar Nota" ──
    canEditNote: canShowEditNote,
    noteId: topNote?.noteId ?? null,
    // ── NOVO: permissão de apagar ──
    canDeleteNote: canShowDelete,
  };

  await nextTick();
  adjustMenuPosition();
}

function adjustMenuPosition() {
  const menu = document.getElementById("twn-context-menu");
  if (!menu) return;

  const menuRect = menu.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let x = contextMenu.value.x;
  let y = contextMenu.value.y;

  // Evita que o menu saia pela direita
  if (x + menuRect.width > vw - 8) {
    x = vw - menuRect.width - 8;
  }
  // Evita que o menu saia pela parte inferior
  if (y + menuRect.height > vh - 8) {
    y = vh - menuRect.height - 8;
  }

  contextMenu.value.x = x;
  contextMenu.value.y = y;
}

function handleContextMenuOpenSettings() {
  closeContextMenu();
  showSettingsModal.value = true;
}

function handleContextMenuDetails() {
  const notes = contextMenu.value.notes;
  closeContextMenu();
  if (notes && notes.length > 0) {
    const topNote = notes[0];
    if (topNote?.noteId) {
      emit("note-details", topNote.noteId);
    }
  }
}

function handleContextMenuEditTag() {
  if (!contextMenu.value.canEditTag || !contextMenu.value.tagId) return;
  tagStore.setIdTag(contextMenu.value.tagId);
  closeContextMenu();
  showEditTagForm.value = true;
}

// ── NOVO: abre o EditNoteForm com a nota do menu de contexto ─────────────────
function handleContextMenuEditNote() {
  if (!contextMenu.value.canEditNote || !contextMenu.value.noteId) return;
  selectedNoteIdForEdit.value = contextMenu.value.noteId;
  closeContextMenu();
  showEditNoteForm.value = true;
}

function handleEditNoteClose() {
  showEditNoteForm.value = false;
  selectedNoteIdForEdit.value = null;
}

// ── NOVO: emite request-delete para o pai (TextDisplayer) tratar ─────────────
function handleContextMenuDeleteNote() {
  if (!contextMenu.value.canDeleteNote || !contextMenu.value.noteId) return;
  const noteId = contextMenu.value.noteId;
  closeContextMenu();
  emit("request-delete", noteId);
}

function handleEditTagRefresh() {
  // EditTagForm já chama tagStore.updateTag que propaga para allNotes/notes
  // Nada adicional necessário aqui
}

// Fecha o menu ao clicar fora ou pressionar Escape
function handleGlobalClick(event) {
  const menu = document.getElementById("twn-context-menu");
  if (menu && !menu.contains(event.target)) {
    closeContextMenu();
  }
}

function handleGlobalKeydown(event) {
  if (event.key === "Escape") {
    closeContextMenu();
  }
}

// ── Seleção de texto com mouse ───────────────────────────────────────────────
const LEFT_BUTTON = 0;
const LEFT_BUTTON_MASK = 1;
const RIGHT_BUTTON = 2;
const RIGHT_BUTTON_MASK = 2;
const LEFT_DRAG_THRESHOLD_PX = 4;
const RIGHT_DRAG_THRESHOLD_PX = 4;
let releaseContextMenuSuppressionTimer = null;
let releaseSelectionMadeSuppressionTimer = null;

const suppressNextContextMenu = ref(false);
const suppressNextSelectionMade = ref(false);

const leftButtonSelection = ref({
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
});

const rightButtonSelection = ref({
  active: false,
  moved: false,
  startX: 0,
  startY: 0,
  anchorRange: null,
});

function getCaretRangeFromPoint(x, y) {
  if (typeof document.caretRangeFromPoint === "function") {
    return document.caretRangeFromPoint(x, y);
  }

  if (typeof document.caretPositionFromPoint === "function") {
    const position = document.caretPositionFromPoint(x, y);
    if (!position) return null;

    const range = document.createRange();
    range.setStart(position.offsetNode, position.offset);
    range.collapse(true);
    return range;
  }

  return null;
}

function isRangeInsideContainer(range) {
  return !!(
    range &&
    containerRef.value &&
    containerRef.value.contains(range.startContainer)
  );
}

function isPointInsideRangeClientRects(range, x, y) {
  if (!range) return false;

  const rects = range.getClientRects();
  for (const rect of rects) {
    if (
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom
    ) {
      return true;
    }
  }

  return false;
}

function isRightClickInsideCurrentTextSelection(event) {
  if (!event || event.button !== RIGHT_BUTTON) return false;

  const selection = window.getSelection();
  if (
    !selection ||
    selection.rangeCount === 0 ||
    selection.isCollapsed ||
    !selection.toString().trim()
  ) {
    return false;
  }

  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);

    if (!containerRef.value || !range.intersectsNode(containerRef.value)) {
      continue;
    }

    if (isPointInsideRangeClientRects(range, event.clientX, event.clientY)) {
      return true;
    }
  }

  return false;
}

function resetLeftButtonSelection() {
  leftButtonSelection.value = {
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
  };
}

function resetRightButtonSelection() {
  rightButtonSelection.value = {
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    anchorRange: null,
  };
}

function temporarilySuppressContextMenu() {
  suppressNextContextMenu.value = true;

  if (releaseContextMenuSuppressionTimer) {
    clearTimeout(releaseContextMenuSuppressionTimer);
  }

  releaseContextMenuSuppressionTimer = window.setTimeout(() => {
    suppressNextContextMenu.value = false;
    releaseContextMenuSuppressionTimer = null;
  }, 350);
}

function temporarilySuppressSelectionMade() {
  suppressNextSelectionMade.value = true;

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
  }

  releaseSelectionMadeSuppressionTimer = window.setTimeout(() => {
    suppressNextSelectionMade.value = false;
    releaseSelectionMadeSuppressionTimer = null;
  }, 500);
}

function clearSelectionMadeSuppression() {
  suppressNextSelectionMade.value = false;

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
    releaseSelectionMadeSuppressionTimer = null;
  }
}

function handleTextMouseDown(event) {
  if (event.button === LEFT_BUTTON) {
    resetRightButtonSelection();

    leftButtonSelection.value = {
      active: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
    };

    return;
  }

  resetLeftButtonSelection();

  if (event.button !== RIGHT_BUTTON) return;

  const anchorRange = getCaretRangeFromPoint(event.clientX, event.clientY);
  if (!isRangeInsideContainer(anchorRange)) {
    resetRightButtonSelection();
    return;
  }

  closeContextMenu();
  rightButtonSelection.value = {
    active: true,
    moved: false,
    startX: event.clientX,
    startY: event.clientY,
    anchorRange: anchorRange.cloneRange(),
  };
}

function updateRightButtonTextSelection(focusRange) {
  const anchorRange = rightButtonSelection.value.anchorRange;
  if (!anchorRange || !isRangeInsideContainer(focusRange)) return;

  try {
    const nextRange = document.createRange();
    const anchorBeforeFocus =
      anchorRange.compareBoundaryPoints(Range.START_TO_START, focusRange) <= 0;

    if (anchorBeforeFocus) {
      nextRange.setStart(anchorRange.startContainer, anchorRange.startOffset);
      nextRange.setEnd(focusRange.startContainer, focusRange.startOffset);
    } else {
      nextRange.setStart(focusRange.startContainer, focusRange.startOffset);
      nextRange.setEnd(anchorRange.startContainer, anchorRange.startOffset);
    }

    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    selection.addRange(nextRange);
  } catch {
    // Evita quebrar o componente se o browser retornar um ponto de caret inválido.
  }
}

function handleLeftButtonMouseMove(event) {
  if (!leftButtonSelection.value.active) return;

  if ((event.buttons & LEFT_BUTTON_MASK) !== LEFT_BUTTON_MASK) {
    resetLeftButtonSelection();
    return;
  }

  const deltaX = Math.abs(event.clientX - leftButtonSelection.value.startX);
  const deltaY = Math.abs(event.clientY - leftButtonSelection.value.startY);

  if (
    !leftButtonSelection.value.moved &&
    deltaX < LEFT_DRAG_THRESHOLD_PX &&
    deltaY < LEFT_DRAG_THRESHOLD_PX
  ) {
    return;
  }

  leftButtonSelection.value.moved = true;
}

function handleRightButtonMouseMove(event) {
  if (!rightButtonSelection.value.active) return;

  if ((event.buttons & RIGHT_BUTTON_MASK) !== RIGHT_BUTTON_MASK) {
    resetRightButtonSelection();
    return;
  }

  const deltaX = Math.abs(event.clientX - rightButtonSelection.value.startX);
  const deltaY = Math.abs(event.clientY - rightButtonSelection.value.startY);

  if (
    !rightButtonSelection.value.moved &&
    deltaX < RIGHT_DRAG_THRESHOLD_PX &&
    deltaY < RIGHT_DRAG_THRESHOLD_PX
  ) {
    return;
  }

  rightButtonSelection.value.moved = true;
  temporarilySuppressContextMenu();
  temporarilySuppressSelectionMade();
  closeContextMenu();
  event.preventDefault();

  const focusRange = getCaretRangeFromPoint(event.clientX, event.clientY);
  updateRightButtonTextSelection(focusRange);
}

function handleRightButtonMouseUp(event) {
  if (!rightButtonSelection.value.active) return;

  const wasRightButtonDrag = rightButtonSelection.value.moved;
  resetRightButtonSelection();

  if (!wasRightButtonDrag) return;

  temporarilySuppressContextMenu();
  temporarilySuppressSelectionMade();
  closeContextMenu();
  event.preventDefault();
}

function handleGlobalContextMenu(event) {
  if (!suppressNextContextMenu.value) return;

  event.preventDefault();
  event.stopPropagation();
  closeContextMenu();
}

// Sem escapeHtml aqui, ao contrário do irmão em texto puro: a árvore é montada com
// h() e vnodes de texto, que o Vue escapa por construção — não há v-html nenhum
// neste renderizador. E isSingleWord vive em utils/htmlNoteSegments.js, junto da
// segmentação que a usa.

// ── Cores das tags ───────────────────────────────────────────────────────────
const fallbackColors = computed(() => {
  const s = settingStore?.lastConsultedSetting;
  return {
    text: s?.uncategorizedTextColor || "#000000",
    bg: s?.uncategorizedBackgroundColor || "#ffffff",
    underline: s?.uncategorizedUnderlineColor || "#000000",
  };
});

function getPrimaryTagIndex(note) {
  const arr = note?.tagIsPrimary;
  if (!Array.isArray(arr) || arr.length === 0) return 0;
  const idx = arr.findIndex((v) => v === true);
  return idx >= 0 ? idx : 0;
}

function pickSpecific(arr, primaryIndex, tagIdsLength) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  if (
    typeof tagIdsLength === "number" &&
    tagIdsLength > 0 &&
    arr.length === tagIdsLength
  ) {
    return arr[primaryIndex] ?? null;
  }
  return arr[0] ?? null;
}

function getNoteVisuals(note) {
  const primaryIndex = getPrimaryTagIndex(note);
  const tagIdsLength = Array.isArray(note?.tagIds) ? note.tagIds.length : 0;

  const specificTextColor = pickSpecific(
    note?.tagSpecificColors,
    primaryIndex,
    tagIdsLength
  );
  const specificBgColor = pickSpecific(
    note?.tagSpecificBackgrounds,
    primaryIndex,
    tagIdsLength
  );
  const specificUnderline = pickSpecific(
    note?.tagSpecificUnderlines,
    primaryIndex,
    tagIdsLength
  );

  const defaults = fallbackColors.value;

  return {
    textColor: specificTextColor || note?.defaultColor || defaults.text,
    bgColor: specificBgColor || note?.defaultBackground || defaults.bg,
    underlineColor:
      specificUnderline || note?.defaultUnderline || defaults.underline,
  };
}

// ── Projeção, segmentação e árvore ───────────────────────────────────────────
const MAX_SEGMENT_TEXT_LENGTH = 4000;

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

/**
 * Atributos que sobrevivem à renderização. O conteúdo já vem normalizado pelo
 * backend, mas o renderizador não confia nisso: ele monta vnodes, e um `on*` que
 * escapasse viraria handler de verdade. É a Regra 2 do normalizador aplicada de
 * novo, na última fronteira.
 */
const RENDERABLE_ATTRS = new Set([
  "class",
  "href",
  "id",
  "lang",
  "dir",
  "colspan",
  "rowspan",
  "colwidth",
  "start",
  "value",
  "src",
  "alt",
]);

/**
 * Vazias que CONTRIBUEM COM UM CARACTERE na projeção — é isso que o conjunto
 * significa, não "não tem filhos".
 *
 * ⚠️ `img` não entra aqui, embora também seja vazia: ela não emite quebra, logo
 * não contribui com caractere nenhum, e o ramo de `renderChildren` que consome
 * este conjunto avança `ctx.offset` em 1. Pô-la aqui deslocaria toda nota depois
 * da primeira imagem. Ela cai no caminho genérico, que é o certo: sem filhos,
 * sem avanço de offset.
 */
const VOID_TAGS = new Set(["br", "hr"]);

/**
 * Prefixo do `id` que a importação usa para nomear a página do original. Espelha
 * `DocxPageMarks.IdPrefix` do backend, já com o `lit-` que o normalizador
 * acrescenta a todo `id` de conteúdo.
 */
const PAGE_ID_PREFIX = "lit-page-";

/**
 * Número da página quando o elemento é uma marca; `null` quando não é.
 *
 * As três condições juntas — âncora, vazia, `id` no padrão — evitam confundir a
 * marca com link comum ou com âncora de nota. O número tem de ser inteiro: `id`
 * é conteúdo do usuário, e qualquer coisa pode aparecer ali.
 */
/**
 * Classe do título corrente do impresso, vinda da importação de PDF. Espelha
 * `HtmlVocabulary.ClassGroups` no backend.
 */
const RUNNING_HEAD_CLASS = "lit-running-head";

/** O bloco é a linha de título corrente, que já traz o fólio no próprio texto. */
function isRunningHead(element) {
  return element?.classList?.contains(RUNNING_HEAD_CLASS) ?? false;
}

function pageOf(element) {
  if (element.localName !== "a" || element.childNodes.length > 0) return null;

  const id = element.getAttribute("id");
  if (!id || !id.startsWith(PAGE_ID_PREFIX)) return null;

  const page = id.slice(PAGE_ID_PREFIX.length);
  return /^\d+$/.test(page) ? page : null;
}

const parsedBody = computed(() => {
  const html = typeof props.content === "string" ? props.content : "";
  if (!html) return null;
  return new DOMParser().parseFromString(html, "text/html").body;
});

// A projeção é a régua: é sobre ela que textRangeStart/End foram gravados, dos
// dois lados. Ver utils/htmlProjection.js.
const projection = computed(() =>
  parsedBody.value ? toPlainText(parsedBody.value) : { text: "", map: [] }
);

const projectionText = computed(() => projection.value.text);

// text node → seu intervalo na projeção, para o recorte não precisar recontar.
const nodeRanges = computed(() => {
  const ranges = new Map();
  for (const segment of projection.value.map) {
    if (segment.node) ranges.set(segment.node, segment);
  }
  return ranges;
});

const segmentation = computed(() =>
  computeNoteSegments(projectionText.value, props.notes)
);

/**
 * Estilo de uma fatia, espelhando as regras do renderizador de texto puro: trecho
 * de várias palavras ganha fundo — arredondado só nas pontas REAIS do trecho, para
 * não criar degraus nas junções internas —, palavra única ganha cor e negrito, e os
 * dois se sobrepõem quando há interseção de ranges.
 */
function pieceStyle(piece) {
  if (!piece.notes.length || !props.hasHighlights) return "";

  const parts = [];
  const multiNote = piece.notes.find((n) => !n.isOriginalSingleWord) || null;
  const singleNote = piece.notes.find((n) => n.isOriginalSingleWord) || null;

  if (multiNote) {
    const { bgColor } = getNoteVisuals(multiNote);
    parts.push(`background-color: ${bgColor};`);

    if (piece.start === multiNote.textRangeStart) {
      parts.push(
        "border-top-left-radius: 0.5rem; border-bottom-left-radius: 0.5rem; padding-left: 0.25rem;"
      );
    }
    if (piece.end === multiNote.textRangeEnd) {
      parts.push(
        "border-top-right-radius: 0.5rem; border-bottom-right-radius: 0.5rem; padding-right: 0.25rem;"
      );
    }
  }

  if (singleNote) {
    const { textColor } = getNoteVisuals(singleNote);
    parts.push(`color: ${textColor}; font-weight: bold;`);
  }

  parts.push("cursor: pointer;");
  return parts.join(" ");
}

/**
 * Abre o link em aba nova **sem tirar o foco da leitura**.
 *
 * Não há API suportada para isso: os navegadores ignoram `blur()` na aba nova de
 * propósito, para um site não conseguir abrir abas escondidas. O que funciona é
 * reproduzir o gesto que o próprio leitor daria — Ctrl+clique, ou Cmd no Mac —,
 * porque aí quem decide é o navegador, seguindo o modificador.
 *
 * O clique é disparado num <a> avulso, e não no elemento da tela, para não voltar
 * a cair neste mesmo handler.
 *
 * ⚠️ **Não é garantido.** Chromium e Firefox honram o modificador; o Safari
 * ignora e abre em primeiro plano. A degradação é benigna: vira exatamente o
 * comportamento de `target="_blank"`, que continua no elemento como rede.
 */
function openInBackgroundTab(href) {
  const isApple = /Mac|iPhone|iPad|iPod/.test(
    navigator.userAgentData?.platform ?? navigator.platform ?? ""
  );

  const link = document.createElement("a");
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.style.display = "none";

  document.body.appendChild(link);
  try {
    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: false,
        cancelable: true,
        view: window,
        ctrlKey: !isApple,
        metaKey: isApple,
      })
    );
  } finally {
    link.remove();
  }
}

function renderableAttrs(element) {
  const attrs = {};
  for (const attr of element.attributes) {
    const name = attr.name.toLowerCase();
    if (RENDERABLE_ATTRS.has(name)) attrs[name] = attr.value;
  }

  // Largura de coluna: o conteúdo guarda o NÚMERO em `colwidth`, e a conversão
  // para CSS acontece aqui, na leitura. O editor faz o equivalente por conta
  // própria, regerando o <colgroup>.
  //
  // Só a primeira célula de cada coluna importa: com `table-layout: fixed`, é a
  // primeira linha que define as larguras. Aplicar nas demais é inofensivo.
  if ((element.localName === "td" || element.localName === "th") && attrs.colwidth) {
    const first = Number.parseInt(String(attrs.colwidth).split(",")[0], 10);
    if (Number.isFinite(first) && first > 0) {
      attrs.style = `width: ${first}px`;
    }
    // O atributo em si não vai para o DOM renderizado: não é HTML válido fora do
    // modelo do Tiptap, e já cumpriu o papel de virar largura.
    delete attrs.colwidth;
  }

  // Abrir link externo em nova aba é decisão de LEITURA, não de conteúdo — por
  // isso `target` e `rel` são acrescentados AQUI e não ficam gravados no HTML.
  // Duas consequências boas: o conteúdo continua com o mínimo que a whitelist
  // aceita, e a regra passa a valer para todo texto já existente, sem precisar
  // reescrever nada.
  //
  // Âncora interna fica de fora: ela deve rolar a própria página, não abrir aba.
  if (element.localName === "a" && attrs.href && !attrs.href.startsWith("#")) {
    attrs.target = "_blank";
    // noopener fecha o acesso da aba nova ao window.opener; noreferrer evita
    // vazar a URL de origem. Ficam como REDE: se o handler abaixo falhar ou o
    // JavaScript não rodar, o link ainda abre em aba nova.
    attrs.rel = "noopener noreferrer";

    const href = attrs.href;
    attrs.onClick = (event) => {
      // Deixa passar o gesto explícito do leitor (Ctrl/Cmd/meio) sem interferir.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      openInBackgroundTab(href);
    };
  }

  // Mesma regra do <a> acima: são decisões de LEITURA, acrescentadas aqui e não
  // gravadas no conteúdo. Valem para todo texto já existente sem reescrever nada.
  //
  // `referrerpolicy` é o que importa, e não é cosmético: a imagem é hotlink, então
  // cada carga é uma requisição ao servidor de origem. Sem isto, ele receberia no
  // Referer a URL da página do Litterae que o leitor está lendo — quem lê o quê
  // vazando para terceiro a cada imagem.
  if (element.localName === "img") {
    attrs.referrerpolicy = "no-referrer";
    attrs.loading = "lazy";
    attrs.decoding = "async";
  }

  return attrs;
}

/**
 * Emite os expoentes cuja posição já foi alcançada. As marcas têm comprimento
 * zero, então saem logo depois da fatia que termina naquele offset.
 */
function flushExponents(ctx, out, upTo) {
  while (
    ctx.next < ctx.exponents.length &&
    ctx.exponents[ctx.next].pos <= upTo
  ) {
    const marker = ctx.exponents[ctx.next];
    ctx.next += 1;

    if (!props.hasExponent) continue;

    out.push(
      h(
        "sup",
        {
          class: "note-exponent text-xs font-bold",
          onClick: () => onNoteClick(marker.notes),
          onContextmenu: (event) => onNoteRightClick(marker.notes, event),
        },
        `${marker.isContinuation ? ", " : ""}${marker.exponent}`
      )
    );
  }
}

function renderTextNode(node, ctx) {
  const range = ctx.ranges.get(node);
  if (!range) return [];

  const pieces = chunkPieces(
    sliceNodeBySegments(range.start, range.end, ctx.segments),
    MAX_SEGMENT_TEXT_LENGTH
  );

  const out = [];

  for (const piece of pieces) {
    const hasNotes = piece.notes.length > 0;
    const interactive = hasNotes && props.hasHighlights;

    out.push(
      h(
        "span",
        {
          "data-offset": piece.start,
          // Ids das notas deste pedaço, para o leitor saber quais estão na tela
          // sem varrer a árvore: o IntersectionObserver do TextDisplayer observa
          // só os spans que TÊM nota — dezenas, não os milhares de pedaços do
          // texto. Sem o atributo, os ids ficariam presos à segmentação, que
          // vive no componente e não no DOM.
          "data-note-ids": hasNotes
            ? piece.notes.map((note) => note.noteId).join(",")
            : undefined,
          style: pieceStyle(piece),
          onClick: interactive ? () => onSegmentClick(piece.notes) : undefined,
          onContextmenu: interactive
            ? (event) => onNoteRightClick(piece.notes, event)
            : undefined,
        },
        ctx.text.slice(piece.start, piece.end)
      )
    );

    flushExponents(ctx, out, piece.end);
  }

  ctx.offset = range.end;
  return out;
}

function renderChildren(parent, ctx) {
  const out = [];

  for (const child of parent.childNodes) {
    if (child.nodeType === TEXT_NODE) {
      out.push(...renderTextNode(child, ctx));
      continue;
    }
    if (child.nodeType !== ELEMENT_NODE) continue;

    const tag = child.localName;

    // Marca de página: âncora vazia cujo `id` nomeia a página do original. É
    // POSIÇÃO, não conteúdo — não avança o offset, não entra na projeção, e por
    // isso não desloca nota nenhuma. O que muda é só o desenho.
    //
    // A âncora marca onde a página COMEÇA, e o rótulo mostra a que ACABOU ali:
    // é a leitura de "número ao final da página", e é a mesma coisa que o rodapé
    // do impresso faz. A primeira não tem antecessora e por isso não rotula.
    const page = pageOf(child);
    if (page !== null) {
      out.push(h("a", renderableAttrs(child)));

      // `span`, e não `div`: a âncora costuma ser acomodada DENTRO do bloco que
      // abre a página, e esse bloco às vezes é um `<h1>`. `<div>` dentro de
      // título é aninhamento inválido; `<span>` é conteúdo de linha legítimo, e
      // o CSS o faz bloco. O tamanho da fonte é fixado lá — herdando, o número
      // saía do tamanho do título.
      //
      // ⚠️ NÃO no título corrente. Um PDF imprime o próprio fólio, e a
      // importação o preserva no texto ("Otto Maria Carpeaux 1568"). Desenhar o
      // rótulo ali punha DOIS números na mesma linha, e ainda por cima
      // diferentes: 1568 no texto e 1567 na margem — porque o rótulo, por
      // convenção herdada do DOCX, nomeia a página que ACABOU no ponto da
      // âncora. Onde o número já está à vista, quem manda é ele.
      if (props.hasPageMarks && Number(page) > 1 && !isRunningHead(parent)) {
        out.push(
          h("span", { class: "lit-page-mark" }, String(Number(page) - 1))
        );
      }

      continue;
    }

    if (VOID_TAGS.has(tag)) {
      // Contribuem com o \n sintético e não têm filhos onde pendurar o expoente;
      // ele sai como irmão.
      ctx.offset += 1;
      out.push(h(tag, renderableAttrs(child)));
      flushExponents(ctx, out, ctx.offset);
      continue;
    }

    const children = renderChildren(child, ctx);

    if (PROJECTION_BREAK_TAGS.has(tag)) {
      ctx.offset += 1;
      // Dentro do bloco, não depois: expoente solto entre dois <p> ficaria órfão.
      flushExponents(ctx, children, ctx.offset);
    }

    out.push(
      h(tag, renderableAttrs(child), children.length ? children : undefined)
    );
  }

  return out;
}

const contentVNodes = computed(() => {
  const body = parsedBody.value;
  if (!body) return [];

  const ctx = {
    text: projectionText.value,
    ranges: nodeRanges.value,
    segments: segmentation.value.segments,
    exponents: segmentation.value.exponents,
    next: 0,
    offset: 0,
  };

  const nodes = renderChildren(body, ctx);

  // Expoente de nota que termina no fim do documento, sem bloco depois.
  flushExponents(ctx, nodes, Number.MAX_SAFE_INTEGER);

  return nodes;
});

// Componente funcional que devolve os vnodes já montados. Ler o computed aqui
// dentro preserva a reatividade: a leitura acontece durante o render do pai.
const RenderedContent = () => contentVNodes.value;

// ── Seleção de texto ─────────────────────────────────────────────────────────
// Mapeia um Range do DOM para offsets na PROJEÇÃO, aparando espaços em branco nas
// extremidades. Retorna { startOffset, endOffset } ou null.
//
// A diferença para o renderizador de texto puro é o closest(). Lá os
// <span data-offset> nunca aninhavam, e varrer todos testando node.contains()
// funcionava. Aqui o vocabulário de classes produz <span> de conteúdo, e o
// renderizador aninha o próprio <span data-offset> dentro dele: vários nós contêm
// o mesmo alvo, e a varredura acabaria pegando o último que casasse em vez do mais
// próximo. closest() sobe a partir do alvo e pára no primeiro — que é o certo.
function mapRangeToContentOffsets(range) {
  if (!range || !containerRef.value) return null;

  const resolve = (container, offsetInContainer) => {
    const element =
      container.nodeType === 3 ? container.parentElement : container;
    const holder = element?.closest?.("[data-offset]");
    if (!holder || !containerRef.value.contains(holder)) return null;

    const base = parseInt(holder.getAttribute("data-offset"), 10);
    if (Number.isNaN(base)) return null;

    // Caso normal: o container é o text node dentro do span, e o offset do range
    // já é relativo a ele.
    if (container.nodeType === 3) return base + offsetInContainer;

    // Container é elemento: o offset conta filhos, não caracteres. Soma o texto
    // dos filhos anteriores.
    let consumed = 0;
    for (let i = 0; i < offsetInContainer && i < container.childNodes.length; i += 1) {
      consumed += container.childNodes[i].textContent?.length ?? 0;
    }
    return base + consumed;
  };

  let startOffset = resolve(range.startContainer, range.startOffset);
  let endOffset = resolve(range.endContainer, range.endOffset);

  const text = projectionText.value;

  // Apara espaços em branco nas extremidades, para que o duplo clique numa palavra
  // seguida de espaço não inclua esse espaço na nota.
  if (startOffset != null && endOffset != null) {
    while (endOffset > startOffset && /\s/.test(text[endOffset - 1])) {
      endOffset -= 1;
    }
    while (startOffset < endOffset && /\s/.test(text[startOffset])) {
      startOffset += 1;
    }
  }

  if (startOffset != null && endOffset != null && startOffset < endOffset) {
    return { startOffset, endOffset };
  }
  return null;
}

// Emite selection-made com proteção contra emissão duplicada. O duplo clique
// dispara mouseup (handleSelection) e, logo em seguida, dblclick; sem essa
// guarda o mesmo trecho poderia abrir o formulário duas vezes.
let lastEmittedSelection = { start: null, end: null, at: 0 };

function emitSelectionMade(startOffset, endOffset) {
  const now = Date.now();
  if (
    lastEmittedSelection.start === startOffset &&
    lastEmittedSelection.end === endOffset &&
    now - lastEmittedSelection.at < 500
  ) {
    return;
  }
  lastEmittedSelection = { start: startOffset, end: endOffset, at: now };

  emit("selection-made", {
    offsetStart: startOffset,
    offsetEnd: endOffset,
    // Da projeção, não do HTML: é o texto que o leitor de fato selecionou.
    selectedText: projectionText.value.slice(startOffset, endOffset),
  });
}

function handleSelection(event) {
  if (event?.button !== LEFT_BUTTON) {
    resetLeftButtonSelection();
    return;
  }

  const wasLeftButtonDrag = leftButtonSelection.value.moved;
  resetLeftButtonSelection();

  // O componente de anotação só deve abrir quando a seleção nasce de arrasto
  // com o botão esquerdo. Clique simples ou mouseup direito não emitem nada.
  if (!wasLeftButtonDrag) return;

  // Quando a seleção foi feita com botão direito arrastado,
  // mantemos apenas a seleção visual e NÃO emitimos selection-made.
  // Isso impede a abertura do formulário/componente de anotação.
  if (suppressNextSelectionMade.value) {
    clearSelectionMadeSuppression();
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (!containerRef.value?.contains(range.commonAncestorContainer)) return;

  const selectedText = selection.toString();
  if (!selectedText || selectedText.trim() === "") return;

  const trimmed = selectedText.replace(/\n+$/g, "");
  if (!trimmed) return;

  const offsets = mapRangeToContentOffsets(range);
  if (!offsets) return;

  const actualSelectedText = projectionText.value.slice(
    offsets.startOffset,
    offsets.endOffset
  );
  if (actualSelectedText.trim() === trimmed.trim()) {
    emitSelectionMade(offsets.startOffset, offsets.endOffset);
  }
}

// ── Duplo clique: encolhe a seleção nativa para não pegar o espaço final ─────
// Ao dar duplo clique numa palavra seguida de espaço, o navegador costuma
// incluir o espaço em branco no fim da seleção. Aqui ajustamos o range para
// que a seleção termine exatamente no último caractere da palavra (também
// aparamos eventuais espaços à esquerda, por segurança).
function handleWordDoubleClick() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

  const range = selection.getRangeAt(0).cloneRange();
  if (!containerRef.value?.contains(range.commonAncestorContainer)) return;

  let changed = false;
  let guard = 0;

  // Apara espaços em branco à direita.
  while (guard < 256) {
    guard += 1;
    const { endContainer, endOffset } = range;
    if (
      endContainer.nodeType === Node.TEXT_NODE &&
      endOffset > 0 &&
      /\s/.test(endContainer.data[endOffset - 1])
    ) {
      range.setEnd(endContainer, endOffset - 1);
      changed = true;
      continue;
    }
    break;
  }

  // Apara espaços em branco à esquerda.
  guard = 0;
  while (guard < 256) {
    guard += 1;
    const { startContainer, startOffset } = range;
    if (
      startContainer.nodeType === Node.TEXT_NODE &&
      startOffset < startContainer.data.length &&
      /\s/.test(startContainer.data[startOffset])
    ) {
      range.setStart(startContainer, startOffset + 1);
      changed = true;
      continue;
    }
    break;
  }

  // Reaplica a seleção ajustada quando algo mudou e ela não colapsou (duplo
  // clique sobre um espaço, em que encolher tudo deixaria a seleção vazia —
  // nesse caso mantemos o comportamento original do navegador e não emitimos).
  if (changed && !range.collapsed) {
    selection.removeAllRanges();
    selection.addRange(range);
  }

  if (range.collapsed) return;

  // Abre o formulário de anotação para a palavra selecionada. handleSelection
  // (no mouseup) não emite em duplo clique por não haver arrasto; é aqui que a
  // seleção por duplo clique vira uma anotação.
  const offsets = mapRangeToContentOffsets(range);
  if (offsets) {
    emitSelectionMade(offsets.startOffset, offsets.endOffset);
  }
}

// ── Fluxo 2: highlight do trecho via highlightTextNoteId ─────────────────────
watch(
  () => noteStore.highlightTextNoteId,
  async (noteId) => {
    if (!noteId) return;

    const note = noteStore.allNotes.find((n) => n.noteId === noteId);
    if (!note) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const start = note.textRangeStart ?? note.textRangeStar ?? null;
    const end = note.textRangeEnd ?? null;
    if (start === 0 && end === 0) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    await nextTick();

    if (!containerRef.value) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const allSpans = containerRef.value.querySelectorAll("[data-offset]");
    const matchingSpans = [];
    for (const span of allSpans) {
      const spanStart = parseInt(span.getAttribute("data-offset"), 10);
      const rawText = span.textContent ?? "";
      if (rawText.trim() === "") continue;
      const spanLen = rawText.length;
      const spanEnd = spanStart + spanLen;
      if (spanStart < end && spanEnd > start) matchingSpans.push(span);
    }

    if (matchingSpans.length === 0) {
      noteStore.highlightTextNoteId = null;
      return;
    }

    const firstSpan = matchingSpans[0];
    const scrollContainer = containerRef.value.closest(
      ".text-scroll-container"
    );
    if (scrollContainer) {
      const spanTop = firstSpan.getBoundingClientRect().top;
      const containerTop = scrollContainer.getBoundingClientRect().top;
      scrollContainer.scrollBy({
        top: spanTop - containerTop - 80,
        behavior: "smooth",
      });
    } else {
      firstSpan.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const allSameParent = matchingSpans.every(
      (s) => s.parentNode === matchingSpans[0].parentNode
    );

    if (allSameParent && matchingSpans.length > 0) {
      const parent = matchingSpans[0].parentNode;
      const anchor = matchingSpans[0];

      const wrapper = document.createElement("mark");
      wrapper.className = "note-highlight-flash";
      wrapper.style.cssText =
        "background: none; padding: 0; margin: 0; display: inline;";

      parent.insertBefore(wrapper, anchor);
      matchingSpans.forEach((s) => wrapper.appendChild(s));

      setTimeout(() => {
        matchingSpans.forEach((s) => parent.insertBefore(s, wrapper));
        wrapper.remove();
        noteStore.highlightTextNoteId = null;
      }, 4000);
    } else {
      matchingSpans.forEach((span) =>
        span.classList.add("note-highlight-flash")
      );
      setTimeout(() => {
        matchingSpans.forEach((span) =>
          span.classList.remove("note-highlight-flash")
        );
        noteStore.highlightTextNoteId = null;
      }, 4000);
    }
  }
);

onMounted(() => {
  scrollAnchorEl.value =
    containerRef.value?.closest(".text-scroll-container") ?? null;

  document.addEventListener("mousemove", handleLeftButtonMouseMove, true);
  document.addEventListener("mousemove", handleRightButtonMouseMove, true);
  document.addEventListener("mouseup", handleRightButtonMouseUp, true);
  document.addEventListener("contextmenu", handleGlobalContextMenu, true);
  document.addEventListener("mouseup", handleSelection);
  document.addEventListener("click", handleGlobalClick);
  document.addEventListener("keydown", handleGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("mousemove", handleLeftButtonMouseMove, true);
  document.removeEventListener("mousemove", handleRightButtonMouseMove, true);
  document.removeEventListener("mouseup", handleRightButtonMouseUp, true);
  document.removeEventListener("contextmenu", handleGlobalContextMenu, true);
  document.removeEventListener("mouseup", handleSelection);
  document.removeEventListener("click", handleGlobalClick);
  document.removeEventListener("keydown", handleGlobalKeydown);

  if (releaseContextMenuSuppressionTimer) {
    clearTimeout(releaseContextMenuSuppressionTimer);
  }

  if (releaseSelectionMadeSuppressionTimer) {
    clearTimeout(releaseSelectionMadeSuppressionTimer);
  }
});
</script>

<template>
  <!--
    Raiz <div>, não <span>: o conteúdo tem blocos, e <p> ou <ul> dentro de <span>
    faria o navegador remontar a árvore — que é justamente o que desalinha os
    offsets. O TextDisplayer envolve este componente em .text-body--html, que
    NÃO tem white-space: pre-wrap.
  -->
  <!--
    `lit-heads-hidden` é o que dá função ao botão de fólio num texto de PDF. Lá
    o número da página está no próprio texto, na linha do título corrente, e o
    rótulo da margem não é desenhado (ver renderChildren) — sem isto o botão não
    teria o que alternar. Com ele, alterna a linha inteira.

    Num texto de DOCX não há título corrente, a classe não casa com nada, e o
    botão segue governando o rótulo da margem como antes.
  -->
  <div
    ref="containerRef"
    :class="{ 'lit-heads-hidden': !hasPageMarks }"
    @mousedown="handleTextMouseDown"
    @dblclick="handleWordDoubleClick"
  >
    <!-- Árvore montada com h(): ver contentVNodes, acima. -->
    <RenderedContent />
  </div>

  <!-- ── Pilha flutuante com os criadores das notas visíveis ─────────────── -->
  <!-- Teleporta para body por conta própria; não interfere no fluxo do texto. -->
  <NoteCreatorsStack :notes="props.notes" :anchor="scrollAnchorEl" />

  <!-- ── Menu de contexto flutuante ──────────────────────────────────────── -->
  <Teleport to="body">
    <div
      v-if="contextMenu.visible"
      id="twn-context-menu"
      class="twn-context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      @click.stop
    >
      <!-- Editar nota (habilitado) -->
      <button
        v-if="contextMenu.canEditNote && contextMenu.noteId"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuEditNote"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar nota</span>
      </button>

      <!-- Editar nota (desabilitado) -->
      <div
        v-if="!contextMenu.canEditNote && contextMenu.noteId"
        class="twn-menu-item twn-menu-item--disabled"
        title="Sem permissão para editar esta nota"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar nota</span>
      </div>

      <!-- Detalhes da nota (sempre visível) -->
      <button
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuDetails"
      >
        <i class="fas fa-file-alt twn-menu-icon"></i>
        <span>Detalhes da nota</span>
      </button>

      <!-- Apagar nota (terceira posição) -->
      <button
        v-if="contextMenu.canDeleteNote && contextMenu.noteId"
        type="button"
        class="twn-menu-item twn-menu-item--danger"
        @click="handleContextMenuDeleteNote"
      >
        <i class="fas fa-trash-alt twn-menu-icon"></i>
        <span>Apagar nota</span>
      </button>

      <!-- Separador entre ações destrutivas e demais -->
      <div class="twn-menu-separator"></div>

      <!-- Editar tag (habilitado) -->
      <button
        v-if="contextMenu.canEditTag && contextMenu.tagId"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuEditTag"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar tag</span>
      </button>

      <!-- Editar tag (desabilitado) -->
      <div
        v-if="!contextMenu.canEditTag || !contextMenu.tagId"
        class="twn-menu-item twn-menu-item--disabled"
        :title="!contextMenu.tagId ? 'Nota sem tag' : 'Sem permissão para editar esta tag'"
      >
        <i class="fas fa-pencil-alt twn-menu-icon"></i>
        <span>Editar tag</span>
      </div>

      <!-- Editar preferências (só quando não há tag e usuário pode editar a nota) -->
      <button
        v-if="!contextMenu.tagId && contextMenu.canEditNote"
        type="button"
        class="twn-menu-item"
        @click="handleContextMenuOpenSettings"
      >
        <i class="fas fa-sliders-h twn-menu-icon"></i>
        <span>Editar preferências</span>
      </button>
    </div>

    <!-- EditTagForm aberto pelo menu de contexto -->
    <EditTagForm
      v-if="showEditTagForm"
      @close="showEditTagForm = false"
      @refresh="handleEditTagRefresh"
    />

    <!-- ── NOVO: EditNoteForm aberto pelo menu de contexto ───────────────── -->
    <EditNoteForm
      v-if="showEditNoteForm && memberCanEditNote"
      :show="showEditNoteForm"
      :noteId="selectedNoteIdForEdit"
      :initialStep="1"
      @close="handleEditNoteClose"
    />

    <SettingsModal
      v-if="showSettingsModal"
      @close="showSettingsModal = false"
    />
  </Teleport>
</template>

<style scoped>
.note-exponent {
  vertical-align: 0.4em;
  cursor: pointer;
  color: var(--text-muted);
}

:global(.note-highlight-flash) {
  animation: text-flash 4s ease-out forwards;
  border-radius: 3px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  padding: 0 2px;
}

@keyframes text-flash {
  0% {
    background-color: var(--warning-bg);
    outline: 2px solid var(--warning);
    outline-offset: 1px;
  }
  60% {
    background-color: var(--warning-bg);
    outline: 2px solid var(--warning);
    outline-offset: 1px;
  }
  100% {
    background-color: transparent;
    outline: 2px solid transparent;
    outline-offset: 1px;
  }
}

/* ── Menu de contexto ───────────────────────────────────────────────────── */
:global(.twn-context-menu) {
  position: fixed;
  z-index: 9999;
  min-width: 150px;
  width: max-content;
  background-color: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
  user-select: none;
}

:global(.twn-menu-item) {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0.4rem 0.65rem;
  font-size: 0.875rem;
  line-height: 1.1;
  color: var(--text-dark);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

:global(.twn-menu-item:hover) {
  background-color: var(--surface-hover);
}

:global(.twn-menu-item--disabled) {
  opacity: 0.38;
  cursor: not-allowed;
  pointer-events: none;
}

:global(.twn-menu-item--danger) {
  color: var(--text-important);
}

:global(.twn-menu-item--danger:hover) {
  background-color: var(--danger-hover-bg);
}

:global(.twn-menu-icon) {
  width: 16px;
  text-align: center;
  font-size: 0.9em;
  flex-shrink: 0;
}

:global(.twn-menu-separator) {
  height: 1px;
  background-color: var(--border-color);
  margin: 0;
}
</style>